import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useTheme } from 'styled-components';
import Button from '#/shared/components/Buttons';
import * as Styled from './styled';

const { PlannerIcon } = Styled;

const handleButtonStyle = (hasFavorites, theme) => {
  if (theme.name === 'Legacy' && hasFavorites) {
    return 'outline-reverse';
  }
  return 'outline';
};

function PlanTripButton(navigateToPlanner, userHasFavoriteStops) {
  const theme = useTheme();

  /** TODO: Remove 'legacy' check when ready to sunset legacy
   * In this case, userHasForiteStops check in the Button variant
   * can be set to just outline.
   * https://trimet.atlassian.net/browse/PRJ77-569
   */

  return (
    <Styled.ButtonWrap>
      <Button
        variant={handleButtonStyle(userHasFavoriteStops, theme)}
        onClick={navigateToPlanner}
      >
        <PlannerIcon />
        <FormattedMessage id="nearbystops.plannerbutton" />
      </Button>
    </Styled.ButtonWrap>
  );
}

function CurrentLocationPrompt({
  isLocationSet,
  sharePosition,
  isGeolocationLoading,
  hasGeolocationError,
  isGeolocationOutOfBounds,
  userHasFavoriteStops,
  navigateToOmnibox,
  navigateToPlanner,
  attemptingToRefreshGeolocation,
  currentLocOrFavorites,
}) {
  const showDefaultPrompt =
    !hasGeolocationError &&
    !isGeolocationOutOfBounds &&
    !attemptingToRefreshGeolocation;
  return (
    <Styled.PromptWrapper
      userHasFavoriteStops={userHasFavoriteStops}
      isLocationSet={isLocationSet}
    >
      {showDefaultPrompt && (
        <>
          <Styled.MobileContainer>
            <Styled.MessageMobile>
              {!userHasFavoriteStops && (
                <FormattedMessage id="location.desktopwelcomeheader" />
              )}
              <FormattedMessage id="location.mobileprompt" />
            </Styled.MessageMobile>
            <Styled.ButtonWrap>
              <Button variant="primary" onClick={sharePosition} rounded>
                <Styled.CurrentLocationIcon />
                <FormattedMessage id="location.mobilepromptbutton" />
              </Button>
            </Styled.ButtonWrap>
            {!currentLocOrFavorites && (
              <Styled.OrWrapper>
                <FormattedMessage id="location.ordivider" />
              </Styled.OrWrapper>
            )}
          </Styled.MobileContainer>
          <Styled.DesktopContainer>
            <Styled.WelcomeContainer>
              <Styled.WelcomeMessage>
                {!userHasFavoriteStops && (
                  <FormattedMessage id="location.desktopwelcomeheader" />
                )}

                <FormattedMessage id="location.desktopwelcomemessage" />
              </Styled.WelcomeMessage>
            </Styled.WelcomeContainer>
          </Styled.DesktopContainer>

          {!currentLocOrFavorites && (
            <Styled.ButtonWrap>
              <Styled.OmniboxButton
                userHasFavoriteStops={userHasFavoriteStops}
                navigateToOmnibox={navigateToOmnibox}
              />
            </Styled.ButtonWrap>
          )}
        </>
      )}

      <Styled.ErrorMessageContainer>
        {isGeolocationLoading && (
          <Styled.LoadingMessage>
            <FormattedMessage id="location.loading" />
          </Styled.LoadingMessage>
        )}
        {hasGeolocationError && (
          <Styled.Message>
            <FormattedMessage id="location.error" />
            {PlanTripButton(navigateToPlanner, userHasFavoriteStops)}
          </Styled.Message>
        )}
        {(isGeolocationOutOfBounds || attemptingToRefreshGeolocation) && (
          <Styled.Message>
            <FormattedMessage id="location.outofbounds" />
            {PlanTripButton(navigateToPlanner, userHasFavoriteStops)}
          </Styled.Message>
        )}
      </Styled.ErrorMessageContainer>
    </Styled.PromptWrapper>
  );
}

CurrentLocationPrompt.propTypes = {
  /** Indicates whether a repeated attempt to find the user is in our
   * service bounds is underway.
   */
  attemptingToRefreshGeolocation: PropTypes.bool,
  /** Whether the current location is set */
  isLocationSet: PropTypes.bool,
  /** Gets the user's current position from the geolocation API */
  sharePosition: PropTypes.func.isRequired,
  /** Determines whether the loading message should be shown */
  isGeolocationLoading: PropTypes.bool.isRequired,
  /** Whether or not an error occurred in accessing geolocation API */
  hasGeolocationError: PropTypes.bool.isRequired,
  /** Whether or not the geolocation is out of bounds */
  isGeolocationOutOfBounds: PropTypes.bool.isRequired,
  /** Whether or not the user has set one or more stops as a favorite */
  userHasFavoriteStops: PropTypes.bool.isRequired,
  /** Navigate to the search route */
  navigateToOmnibox: PropTypes.func.isRequired,
  /** Navigate to the Trip Planner trip tool */
  navigateToPlanner: PropTypes.func.isRequired,
  /** Whether or not there are favorites or current location is established */
  currentLocOrFavorites: PropTypes.bool,
};

CurrentLocationPrompt.defaultProps = {
  attemptingToRefreshGeolocation: false,
  isLocationSet: null,
  currentLocOrFavorites: false,
};

export default CurrentLocationPrompt;
export { PlannerIcon };
